import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AttachmentPreviewContent from 'components/Dashboard/components/AttachmentPreviewContent';
import mobileCheck from 'components/Dashboard/helpers/mobileCheck';
import Loading from 'components/Login/Loading';
import useShareDocumentCalls from 'components/ShareDocument/useShareDocumentCalls';

const getDocumentFaviconUrl = () => `${import.meta.env.VITE_APP_CDNURL}/images/document-scope-favicon.svg`;

const getSharedDocumentPayload = (response) => {
  if (!response || response?.name === 'CanceledError') {
    return null;
  }

  const payload = response?.data ?? response;

  return {
    documentUrl: payload?.documentUrl ?? payload?.DocumentUrl ?? '',
    fileName: payload?.fileName ?? payload?.FileName ?? 'Document',
  };
};

const ShareDocumentPage = () => {
  const { code } = useParams();
  const { getSharedDocument } = useShareDocumentCalls();
  const [documentUrl, setDocumentUrl] = useState('');
  const [filename, setFilename] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    document.getElementById('policymogul-cookie-item')?.remove();

    if (window.Intercom) {
      window.Intercom('update', { hide_default_launcher: true });
    }

    return () => {
      if (window.Intercom) {
        window.Intercom('update', { hide_default_launcher: mobileCheck() });
      }
    };
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    const iconLink = document.querySelector('link[rel="icon"]');
    const appleTouchIconLink = document.querySelector('link[rel="apple-touch-icon"]');
    const previousIconHref = iconLink?.getAttribute('href');
    const previousAppleTouchIconHref = appleTouchIconLink?.getAttribute('href');
    const shouldUseDocumentFavicon = !isLoading && !notFound && !!documentUrl;

    if (!isLoading && !notFound && documentUrl && filename) {
      document.title = filename;
    } else if (!isLoading && (notFound || !documentUrl)) {
      document.title = 'Document not found';
    }

    if (shouldUseDocumentFavicon) {
      const documentFaviconUrl = getDocumentFaviconUrl();
      iconLink?.setAttribute('href', documentFaviconUrl);
      appleTouchIconLink?.setAttribute('href', documentFaviconUrl);
    }

    return () => {
      document.title = previousTitle;

      if (previousIconHref) {
        iconLink?.setAttribute('href', previousIconHref);
      }

      if (previousAppleTouchIconHref) {
        appleTouchIconLink?.setAttribute('href', previousAppleTouchIconHref);
      }
    };
  }, [filename, isLoading, notFound, documentUrl]);

  const handleDownload = async () => {
    if (!documentUrl || isDownloading) {
      return;
    }

    setIsDownloading(true);

    try {
      const response = await fetch(documentUrl);

      if (!response.ok) {
        throw new Error(`Failed to download document. Status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobURL = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobURL;
      anchor.style = 'display: none';

      if (filename?.length) {
        anchor.download = filename;
      }

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(blobURL), 0);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    const source = new AbortController();
    let isCancelled = false;

    const loadSharedDocument = async () => {
      if (!code) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setNotFound(false);

      const response = await getSharedDocument({ code, requestSource: source });

      if (isCancelled) {
        return;
      }

      const sharedDocument = getSharedDocumentPayload(response);

      if (sharedDocument?.documentUrl) {
        setDocumentUrl(sharedDocument.documentUrl);
        setFilename(sharedDocument.fileName);
        setNotFound(false);
      } else {
        setNotFound(true);
      }

      setIsLoading(false);
    };

    loadSharedDocument();

    return () => {
      isCancelled = true;
      source.abort();
    };
  }, [code]);

  if (isLoading) {
    return (
      <div className='share-document-page'>
        <Loading />
      </div>
    );
  }

  if (notFound || !documentUrl) {
    return (
      <div className='share-document-page main-content-wrapper px-3 py-5'>
        <div className='box-top-rounded bg-white p-4 p-lg-5 mx-auto' style={{ maxWidth: '720px' }}>
          <h1 className='title-h3-bold mb-3'>Document not found</h1>
          <p className='paragraph-general main-subtle-text mb-0'>
            This share link is invalid, expired, or no longer available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='share-document-page main-content-wrapper mx-auto px-3 px-lg-5 pt-4 pt-lg-5 pb-2'>
      <div className='share-document-header d-flex align-items-center justify-content-between mb-4 mb-lg-5'>
        <img
          src={`${import.meta.env.VITE_APP_CDNURL}/images/scope_logo.png`}
          alt='Scope logo'
          style={{ width: '150px' }}
          className='rounded'
        />
        <button
          type='button'
          className='share-document-download general-button px-4 specific-y-padding-5 rounded-pill d-flex align-items-center border'
          disabled={isDownloading}
          onClick={handleDownload}
        >
          {isDownloading ? (
            <i className='fas fa-spinner fa-spin' />
          ) : (
            <>
              <span className='icon-arrow-download mr-2' />
              Download
            </>
          )}
        </button>
      </div>
      <div className='share-document-preview border'>
        <AttachmentPreviewContent url={documentUrl} filename={filename} fullPdfPreview containedPreview />
      </div>
    </div>
  );
};

export default ShareDocumentPage;
